import { createContext, useContext, useEffect, useMemo, useState } from "react";

import client from "../api/client";
import { cacheClearAll } from "../lib/cache";
import { draftClearAll } from "../lib/draftStore";
import { supabase } from "../lib/supabase";

const AuthContext = createContext(null);

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? "";

// Race guard for invite-driven registration (InviteAcceptPage.tsx).
//
// supabase.auth.signUp()/signInWithPassword() fire onAuthStateChange(SIGNED_IN)
// with a real Supabase session *before* the backend account necessarily
// exists — the invite page still has to call
// POST /invites/{token}/register to create/activate it. If the listener
// below reacts to that SIGNED_IN event by calling syncSession() (which hits
// POST /auth/session), the backend account may not exist yet and that call
// 401s. Without this guard clearLocalAuth() would run and look like a
// logout mid-registration.
//
// InviteAcceptPage sets this flag before calling into Supabase auth and
// clears it once its own register call has settled (success or failure) —
// by then the backend account exists (or the flow failed and there's
// nothing valid to sync anyway), so the next natural auth event resolves
// normally.
let inviteRegistrationInFlight = false;
export function setInviteRegistrationInFlight(value) {
  inviteRegistrationInFlight = value;
}

async function establishBackendSession(accessToken) {
  const response = await fetch(`${apiBaseUrl}/auth/session`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error("Could not establish backend session");
  }
  return response.json();
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(true);

  const isAuthenticated = Boolean(user);

  const setStoredUser = (authUser) => {
    setUser(authUser);
  };

  const clearLocalAuth = () => {
    cacheClearAll();
    draftClearAll();
    setUser(null);
  };

  const refreshMe = async () => {
    const response = await client.get("/auth/me");
    setStoredUser(response.data);
    return response.data;
  };

  const syncSession = async (accessToken) => {
    await establishBackendSession(accessToken);
    return refreshMe();
  };

  useEffect(() => {
    let mounted = true;

    const bootstrap = async () => {
      try {
        await refreshMe();
        return;
      } catch {
        // No valid httpOnly cookie yet — try Supabase session exchange.
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!mounted) return;
      if (!session?.access_token) {
        clearLocalAuth();
        return;
      }

      try {
        await syncSession(session.access_token);
      } catch {
        clearLocalAuth();
      }
    };

    bootstrap().finally(() => {
      if (mounted) setBootstrapping(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;
      if (event === "SIGNED_OUT" || !session?.access_token) {
        clearLocalAuth();
        return;
      }
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        if (inviteRegistrationInFlight) {
          // InviteAcceptPage owns this sign-in and will call refreshMe()
          // itself once /invites/{token}/register succeeds. Don't race it.
          return;
        }
        try {
          await syncSession(session.access_token);
        } catch {
          clearLocalAuth();
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      const shouldFallbackToBackend =
        error?.name === "AuthApiError" && /invalid login credentials/i.test(error?.message || "");
      if (shouldFallbackToBackend) {
        cacheClearAll();
        draftClearAll();
        const response = await client.post("/auth/login", { email, password });
        setStoredUser(response.data.user);
        return;
      }
      throw error;
    }
    if (!data.session?.access_token) {
      throw new Error("Could not obtain Supabase session");
    }
    cacheClearAll();
    draftClearAll();
    await syncSession(data.session.access_token);
  };

  const logout = async () => {
    try {
      await client.post("/auth/logout");
    } catch {
      // best-effort
    }
    await supabase.auth.signOut();
    clearLocalAuth();
  };

  const value = useMemo(
    () => ({
      user,
      isAuthenticated,
      bootstrapping,
      login,
      logout,
      refreshMe,
      setStoredUser,
    }),
    [user, isAuthenticated, bootstrapping],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
